import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ArrowRight, BadgeInfo, CalendarIcon, Minus, Plus } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Button } from '@ui/button.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Calendar } from '@ui/calendar.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/popover.tsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';
import { Alert, AlertDescription, AlertTitle } from '@ui/alert.tsx';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { cn } from '@r/utils/utils.ts';
import type { FC } from 'react';
import { type BoosterDraftData, getCreateBoosterSchema } from './create-booster-form-schema.ts';

export type BoosterFormField = keyof BoosterDraftData;

function RequiredMark() {
  return (
    <span className="text-destructive" aria-hidden>
      {' '}
      *
    </span>
  );
}

function InfoTooltip({ content, ariaLabel }: { content: string; ariaLabel: string }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex justify-center items-center text-primary hover:text-primary/80"
            aria-label={ariaLabel}
          >
            <BadgeInfo className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface CreateBoosterFormProps {
  onNext: (draft: BoosterDraftData) => void;
  defaultValues?: BoosterDraftData;
  disabledFields?: BoosterFormField[];
  submitLabel?: string;
  showLockedFieldsMessage?: boolean;
  isSubmitting?: boolean;
}

/** Formulaire de saisie des informations générales du booster (création ou édition). */
export const CreateBoosterForm: FC<CreateBoosterFormProps> = ({
  onNext,
  defaultValues,
  disabledFields = [],
  submitLabel,
  showLockedFieldsMessage = false,
  isSubmitting = false
}) => {
  const { t } = useTranslation();
  const isFieldDisabled = (field: BoosterFormField) => disabledFields.includes(field);
  const createBoosterSchema = getCreateBoosterSchema(t);

  const form = useForm<BoosterDraftData>({
    resolver: zodResolver(createBoosterSchema),
    defaultValues: defaultValues ?? {
      title: '',
      description: '',
      startDate: undefined,
      endDate: undefined,
      coefficient: 2
    }
  });

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form]);

  const startDate = form.watch('startDate');
  const endDate = form.watch('endDate');
  const coefficient = form.watch('coefficient');

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onNext)} className="space-y-6">
        {showLockedFieldsMessage && (
          <Alert>
            <AlertTitle>{t('boosters.edit.locked.title')}</AlertTitle>
            <AlertDescription>{t('boosters.edit.locked.description')}</AlertDescription>
          </Alert>
        )}

        <FormField
          control={form.control}
          name="title"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>
                {t('boosters.create.name')}
                <RequiredMark />
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t('boosters.create.name.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                  disabled={isFieldDisabled('title')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('boosters.create.description')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('boosters.create.description.placeholder')}
                  {...field}
                  error={!!fieldState.error}
                  rows={4}
                  disabled={isFieldDisabled('description')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="coefficient"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-1.5">
                <FormLabel>
                  {t('boosters.create.coefficient.label')}
                  <RequiredMark />
                </FormLabel>
                <InfoTooltip
                  content={t('boosters.create.coefficient.info')}
                  ariaLabel={t('boosters.create.coefficient.info.aria')}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {t('boosters.create.coefficient.help')}
              </p>
              <FormControl>
                {isFieldDisabled('coefficient') ? (
                  <Input
                    readOnly
                    value={field.value}
                    disabled
                    className="max-w-[200px] text-center"
                    aria-label={t('boosters.create.coefficient.label')}
                  />
                ) : (
                  <div className="flex max-w-[200px] items-center">
                    <Button
                      type="button"
                      variant="outline"
                      className="px-3 rounded-r-none"
                      onClick={() => field.onChange(Math.max(2, field.value - 1))}
                      disabled={field.value <= 2}
                      aria-label={t('boosters.create.coefficient.decrease.aria')}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      readOnly
                      value={field.value}
                      className="rounded-none border-x-0 text-center [appearance:textfield]"
                      aria-label={t('boosters.create.coefficient.label')}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="px-3 rounded-l-none"
                      onClick={() => field.onChange(field.value + 1)}
                      aria-label={t('boosters.create.coefficient.increase.aria')}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>
                  {t('boosters.create.start.date')}
                  <RequiredMark />
                </FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          'w-full pl-3 text-left font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                        disabled={isFieldDisabled('startDate')}
                      >
                        {field.value ? (
                          format(field.value, 'PPP')
                        ) : (
                          <span>{t('boosters.create.date.pick')}</span>
                        )}
                        <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  {!isFieldDisabled('startDate') && (
                    <PopoverContent className="p-0 w-auto" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => {
                          const startOfToday = new Date();
                          startOfToday.setHours(0, 0, 0, 0);
                          return endDate ? date >= endDate : date < startOfToday;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  )}
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>
                  {t('boosters.create.end.date')}
                  <RequiredMark />
                </FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          'w-full pl-3 text-left font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                        disabled={isFieldDisabled('endDate')}
                      >
                        {field.value ? (
                          format(field.value, 'PPP')
                        ) : (
                          <span>{t('boosters.create.date.pick')}</span>
                        )}
                        <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  {!isFieldDisabled('endDate') && (
                    <PopoverContent className="p-0 w-auto" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => {
                          const startOfToday = new Date();
                          startOfToday.setHours(0, 0, 0, 0);
                          return startDate ? date <= startDate : date < startOfToday;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  )}
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          rounded="full"
          type="submit"
          className="w-full"
          loading={isSubmitting}
          disabled={!isFieldDisabled('coefficient') && coefficient < 2}
        >
          {submitLabel ?? t('boosters.create.submit')}{' '}
          {!submitLabel && <ArrowRight className="ml-2 w-4 h-4" />}
        </Button>
      </form>
    </Form>
  );
};
